export type RequestLog = {
    bodyHash?: string;
    duplicateType?: 'server' | 'client-from-server' | 'client';
    duration?: number;
    endTime?: number;
    error?: string;
    id: string;
    method: string;
    requestId?: string;
    side: 'server' | 'client';
    startTime: number;
    status?: number;
    url: string;
    waterfall?: number;
};

export type RequestSummary = {
    requests: number;
    totalTime: number;
    waterfalls: number;
};

// Global store for collecting requests during server rendering
let serverRequests: RequestLog[] = [];
let requestCounter = 0;

// elapsed time (in milliseconds) to consider next api call in another waterfall
const WATERFALL_THRESHOLD = 70;

/**
 * Generates a unique ID for each request
 */
export const generateRequestId = (): string => {
    requestCounter += 1;
    return `req_${requestCounter}_${Date.now()}`;
};

/**
 * Adds a request to the server-side store
 */
export const addServerRequest = (request: RequestLog): void => {
    serverRequests.push(request);
};

/**
 * Gets all server requests and clears the store
 */
export const getAndClearServerRequests = (): RequestLog[] => {
    const requests = [...serverRequests];
    serverRequests = [];
    requestCounter = 0;
    return requests;
};

/**
 * Calculate the number of request waterfalls among all api calls made to load page data
 */
const calculateNumRequestWaterfalls = (requests: RequestLog[]): number => {
    if (requests.length === 0) {
        return 0;
    }

    // Sort requests by start time to ensure proper waterfall calculation
    const sortedRequests = [...requests].sort((a, b) => a.startTime - b.startTime);
    let numWaterfalls = 0;

    sortedRequests.forEach((req, i) => {
        // Handle last request - just assign current waterfall number
        if (i === sortedRequests.length - 1) {
            req.waterfall = numWaterfalls;
            return;
        }

        // Handle first request - always starts a new waterfall
        if (i === 0) {
            numWaterfalls++;
            req.waterfall = numWaterfalls;
            return;
        }

        // For all other requests, assign current waterfall number first
        const currentStartTime = req.startTime;
        const nextStartTime = sortedRequests[i + 1].startTime;
        req.waterfall = numWaterfalls;

        // Check if next request starts WATERFALL_THRESHOLD or more ms after current request
        // If so, the next request will start a new waterfall
        if (currentStartTime + WATERFALL_THRESHOLD <= nextStartTime) {
            numWaterfalls++;
        }
    });

    return numWaterfalls;
};

/**
 * Calculates summary statistics for a list of requests
 */
export const calculateSummary = (requests: RequestLog[]): RequestSummary => {
    const completedRequests = requests.filter((req) => req.endTime);
    const numWaterfalls = calculateNumRequestWaterfalls(requests);

    return {
        requests: requests.length,
        totalTime: completedRequests.reduce((sum, req) => sum + (req.duration ?? 0), 0),
        waterfalls: numWaterfalls,
    };
};

/**
 * Formats duration in milliseconds to a readable string
 */
export const formatDuration = (ms: number): string => {
    return `${Math.round(ms)}ms`;
};

/**
 * Simple hash function for request body comparison
 */
export const hashRequestBody = (body: any): string => {
    if (!body) {
        return '';
    }

    if (body instanceof FormData) {
        // Convert FormData to string representation
        const entries: string[] = [];
        body.forEach((value, key) => {
            if (value instanceof File) {
                // For files, create a consistent representation
                entries.push(
                    `${key}=${JSON.stringify({
                        _type: 'File',
                        name: value.name,
                        size: value.size,
                        type: value.type,
                    })}`
                );
            } else {
                // For strings and other values, JSON.stringify to handle edge cases
                // Note: FormData converts objects to strings before storing them
                entries.push(`${key}=${JSON.stringify(value)}`);
            }
        });
        // Sort entries using localeCompare for consistent alphabetical ordering
        entries.sort((a, b) => a.localeCompare(b));
        return simpleHash(entries.join('&'));
    }

    if (typeof body === 'object') {
        return simpleHash(JSON.stringify(body));
    }

    return simpleHash(String(body));
};

/**
 * Simple string hash function
 */
const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(36);
};

/**
 * Check if two requests are duplicates
 */
export const areRequestsDuplicate = (req1: RequestLog, req2: RequestLog): boolean => {
    // Must have same method and URL (including query params)
    if (req1.method !== req2.method || req1.url !== req2.url) {
        return false;
    }

    // For POST/PUT/PATCH requests, also check body hash
    if (
        (req1.method === 'POST' || req1.method === 'PUT' || req1.method === 'PATCH') &&
        req1.bodyHash &&
        req2.bodyHash
    ) {
        return req1.bodyHash === req2.bodyHash;
    }

    return true;
};

/**
 * Find duplicate requests and mark them
 */
export const markDuplicateRequests = (requests: RequestLog[]): RequestLog[] => {
    const markedRequests = [...requests];
    const seen = new Map<string, { index: number; side: 'server' | 'client' }>();

    markedRequests.forEach((request, index) => {
        const key = `${request.method}-${request.url}-${request.bodyHash ?? ''}`;
        const existing = seen.get(key);

        if (existing && existing.index !== index) {
            // Mark as duplicate based on where it originated and where it's duplicated
            if (request.side === 'server' && existing.side === 'server') {
                request.duplicateType = 'server';
            } else if (request.side === 'client' && existing.side === 'server') {
                request.duplicateType = 'client-from-server';
            } else if (request.side === 'client' && existing.side === 'client') {
                request.duplicateType = 'client';
            }
        } else {
            seen.set(key, { index, side: request.side });
        }
    });

    return markedRequests;
};
